/**
 * @file - Uber Disconnect Dialog Component.
 */
import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DriverService } from '../../services/driver/driver.service';

/**
 * Uber Disconnect Dialog Component.
 */
@Component({
    standalone: false,
    selector: 'octo-uber-disconnect-dialog',
    templateUrl: './uber-disconnect-dialog.component.html',
    styleUrls: ['./uber-disconnect-dialog.component.scss'],
})
export class UberDisconnectDialogComponent {
    surveyForm: UntypedFormGroup;
    isDisconnecting = false;
    
    surveyMessage = `
        Unlinking your Uber account disables JourneyTV for riders and ends tablet point earnings. You'll need to relink your Uber account to restore full functionality and resume earning with Octopus.
        <div class="mt-4">
        Why are you disconnecting your Uber account from Octopus?*</div>
    `;
    
    disconnectReasons = [
        { value: 'STOPPED_UBER', label: 'Stopped driving with Uber' },
        { value: 'TECHNICAL_ISSUES', label: 'Technical issues with the Uber JourneyTV experience' },
        { value: 'DIDNT_LIKE_FEATURE', label: "I didn't like a particular feature in the Uber JourneyTV experience (please explain below)" },
        { value: 'RIDERS_DIDNT_LIKE', label: "My riders didn't like the JourneyTV experience" },
        { value: 'OTHER', label: 'Something else (please explain below)' }
    ];

    /**
     * Constructor.
     *
     * @param {MatDialogRef<UberDisconnectDialogComponent>} dialogRef - Dialog reference.
     * @param {UntypedFormBuilder} fb - Form builder.
     * @param {DriverService} driverService - Driver service.
     * @param {any} data - Dialog data.
     */
    constructor(
        private dialogRef: MatDialogRef<UberDisconnectDialogComponent>,
        private fb: UntypedFormBuilder,
        private driverService: DriverService,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.surveyForm = this.fb.group({
            reason: ['', Validators.required],
            otherComments: ['', [Validators.maxLength(500)]]
        });
    }

    /**
     * Cancel and close dialog.
     */
    cancel(): void {
        this.dialogRef.close(null);
    }

    /**
     * Confirm disconnect.
     */
    confirm(): void {
        if (this.surveyForm.valid) {
            this.isDisconnecting = true;
            
            // Prepare URL with query parameters
            let url = 'driver/uber/connect/';
            const params = new URLSearchParams();
            
            const reason = this.surveyForm.get('reason')?.value;
            const otherComments = this.surveyForm.get('otherComments')?.value;
            
            if (reason) {
                params.append('disconnection_reason', reason);
            }
            if (otherComments) {
                params.append('disconnection_comment', otherComments);
            }
            
            // Get source from data if available
            if (this.data?.source) {
                params.append('source', this.data.source);
            }
            
            if (params.toString()) {
                url = `${url}?${params.toString()}`;
            }
            
            //Call disconnect API
            this.driverService.removeUberAccountWithSurvey(url).subscribe({
                next: () => {
                    // Close dialog with success
                    this.dialogRef.close({
                        confirmed: true,
                        success: true
                    });
                },
                error: (error) => {
                    this.isDisconnecting = false;
                    // You can add error handling here (e.g., show error message)
                    console.error('Error disconnecting Uber account:', error);
                }
            });
        } else {
            // Mark all fields as touched to trigger validation messages
            Object.keys(this.surveyForm.controls).forEach(key => {
                this.surveyForm.get(key)?.markAsTouched();
            });
        }
    }
}
